import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { theme } from '../../../theme/colors';
import SupplierStatusBadge from '../components/SupplierStatusBadge';
import {
  PoProposalLine,
  formatPoDate,
  formatPoStatus,
  getPoByRespondToken,
  poDateToInput,
  proposeByRespondToken,
  todayInput,
} from '../services/supplierApi';
import '../styles/supplier-module.css';

interface PoItem {
  id: number;
  ingredientName: string;
  quantity: number;
  unitPrice: number;
  unit?: string;
}

interface PoDetail {
  id: number;
  restroId: number;
  poNumber: string;
  status: string;
  supplierName?: string;
  expectedDate?: string | number;
  items?: PoItem[];
}

const PORespond: React.FC = () => {
  const { token } = useParams();
  const [po, setPo] = useState<PoDetail | null>(null);
  const [lines, setLines] = useState<PoProposalLine[]>([]);
  const [deliveryDate, setDeliveryDate] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Invalid link');
      return;
    }
    getPoByRespondToken(token)
      .then((data) => {
        const loaded = data.po as PoDetail;
        setPo(loaded);
        setLines((loaded.items || []).map((item) => ({
          poItemId: item.id,
          proposedQty: item.quantity,
          proposedPrice: item.unitPrice,
        })));
        const initialDate = poDateToInput(loaded.expectedDate);
        setDeliveryDate(initialDate && initialDate >= todayInput() ? initialDate : '');
      })
      .catch((e: Error) => setError(e.message));
  }, [token]);

  const updateLine = (poItemId: number, field: 'proposedQty' | 'proposedPrice', value: string) => {
    const num = value === '' ? undefined : Number(value);
    setLines((prev) => prev.map((l) => (l.poItemId === poItemId ? { ...l, [field]: num } : l)));
  };

  const handleSubmit = async () => {
    if (!token) return;
    setBusy(true);
    setError('');
    try {
      await proposeByRespondToken(token, { lines, deliveryDate: deliveryDate || undefined });
      setMessage('Your proposal was sent to the restaurant.');
      setDone(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sp-respond">
      <header className="sp-respond__header">
        <div className="sp-respond__brand">
          <img src={theme.logo.url} alt="Peppar" />
          <span>Peppar Supplier</span>
        </div>
      </header>
      <main className="sp-respond__main">
        <section className="sp-card">
          <header className="sp-card__header">
            <div className="sp-section-heading">
              <div className="sp-section-heading__row">
                <i className="fa fa-file-text-o" aria-hidden="true" />
                <h2>{po ? `Respond to PO ${po.poNumber}` : 'Purchase order response'}</h2>
              </div>
              {po && <p className="sp-section-heading__sub">{formatPoStatus(po.status)}</p>}
            </div>
          </header>
          <div className="sp-card__body">
            {!po && !error && <div className="sp-loading">Loading purchase order…</div>}
            {error && <div className="sp-alert sp-alert--error">{error}</div>}
            {po && !done && (
              <>
                <div style={{ marginBottom: 14 }}>
                  <SupplierStatusBadge status={po.status} />
                </div>
                {po.status !== 'PENDING_SUPPLIER' ? (
                  <p className="sp-muted">This order is no longer awaiting a supplier response.</p>
                ) : (
                  <>
                    <div className="sp-table-wrap">
                      <table className="sp-table">
                        <thead>
                          <tr>
                            <th>Ingredient</th>
                            <th>Ordered</th>
                            <th>Price</th>
                            <th>Your qty</th>
                            <th>Your price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(po.items || []).map((item) => {
                            const line = lines.find((l) => l.poItemId === item.id);
                            return (
                              <tr key={item.id}>
                                <td>{item.ingredientName}</td>
                                <td>{item.quantity}</td>
                                <td>₹{item.unitPrice}</td>
                                <td>
                                  <input
                                    className="sp-input"
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={line?.proposedQty ?? ''}
                                    onChange={(e) => updateLine(item.id, 'proposedQty', e.target.value)}
                                  />
                                </td>
                                <td>
                                  <input
                                    className="sp-input"
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={line?.proposedPrice ?? ''}
                                    onChange={(e) => updateLine(item.id, 'proposedPrice', e.target.value)}
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="sp-form-row">
                      <label htmlFor="respondDeliveryDate">
                        Proposed delivery date
                        {po.expectedDate != null && (
                          <span className="sp-muted"> (restaurant needs it by {formatPoDate(po.expectedDate)})</span>
                        )}
                        <input
                          id="respondDeliveryDate"
                          className="sp-input"
                          type="date"
                          min={todayInput()}
                          value={deliveryDate}
                          onChange={(e) => setDeliveryDate(e.target.value)}
                          style={{ maxWidth: 220 }}
                        />
                      </label>
                    </div>
                    <div className="sp-actions">
                      <button
                        type="button"
                        className="sp-btn sp-btn--primary"
                        disabled={busy}
                        onClick={handleSubmit}
                      >
                        Submit proposal
                      </button>
                    </div>
                  </>
                )}
              </>
            )}
            {message && <div className="sp-alert sp-alert--success">{message}</div>}
            {done && (
              <p className="sp-muted">
                You can also <a href="/signin" className="sp-link">sign in</a> to the supplier portal to track this order.
              </p>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default PORespond;
